import { QueryParametersInterface } from "@api/repositories/interfaces/QueryParametersInterface";
import { TicketService } from "@share/common/services/ticket/TicketService";
import { ReportState, IdeaReports, ProblemReports, ReportFilterState, DatePickerRangeDataInterface, TicketReport } from "@share/ticketSystem/components/reports/types";
import { TicketStatusCode } from "@share/ticketSystem/enums/TicketStatusCode";
import { TicketType } from "@share/ticketSystem/enums/TicketTypes";
import { ideaReportsList, problemReportsList } from "../data/reportsList";
import { DateTime } from "luxon";

/**
 * all common fields for report
 */
const fullFieldsList = [
    'id',
    'title',
    'description',
    'imageFileIds',
    'fileIds',
    'ownerId',
    'responsibleUserId',
    'status',
    'category',
    'responsibleForImplementationUserId',
    'createdAt',
    'company',
    'branch',
    'direction'
]

/**
 * common fields for some reports
 */
const commonFieldsSequance = [
    'createdAt',
    'direction',
    'title',
    'description',
]

export class TicketReportHelper implements IdeaReports, ProblemReports {

    constructor(
        private readonly state: ReportState,
        private readonly ticketService: TicketService) {
    }

    getReportsList(ticketType: TicketType): Record<string, TicketReport> {
        if (ticketType === TicketType.idea) {
            return ideaReportsList
        }
        else {
            return problemReportsList
        }
    }

    /**
     * 🟩
     * @report1
     *  Список всех поданных идей
     */
    reportIL1(query: QueryParametersInterface) {
        const ideaFields = [
            'costs',
            'solution',
            'hasBigEffect',
            'canBeReplicated',
            'committeeReviewedAt',
            'publishedAt',
            'bigCommitteeReviewedAt',
            'implementedAt',
            'effectFormation'
        ];

        query.include = [...fullFieldsList, ...ideaFields];
        return query;
    }

    /**
     * 🟩
     * @report2 - Отчет для линейных руководителей
     * Список поданных идей с группировкой по подразделению и подробной информацией (дата, ФИО автора, подразделение, текст, статус, ответственный).
     */
    reportIL2(query: QueryParametersInterface) {

        query.include = [
            ...commonFieldsSequance,
            'status',
            'responsibleForImplementationUserId'
        ];

        return query;
    }

    /**
     * 🟩
     * @report3 - Отклоненные идеи
     * Список идей со статусом “Отклонено” с группировкой по подразделению и подробной информацией.
     */
    async reportIL3(query: QueryParametersInterface) {
        query.include = [
            ...commonFieldsSequance,
            'status',
            // 'responsibleUserId'
        ];

        const includeStatusIds = await this.getStatuses(TicketStatusCode.ideas, ["ideas.rejectedByBigCommittee", "ideas.rejectedByCommittee", "ideas.rejectedByResponsible"]);

        if (includeStatusIds?.length) {
            query.where.status = {
                $in: includeStatusIds
            }
        }

        return query;
    }

    /**
     * 🟩
     * @reportIL4 - Сроки реализации идей
     * Список идей со статусами “Принять к реализации“ и “Реализовано“ с информацией о датах.
     */
    async reportIL4(query: QueryParametersInterface) {

        query.include = [
            ...commonFieldsSequance,
            'status',
            'responsibleForImplementationUserId',
            //statuses
            'committeeReviewedAt',
            'implementedAt',
            'plannedImplementationDate'
        ]

        const includeStatusIds = await this.getStatuses(TicketStatusCode.ideas, ["ideas.implemented", "ideas.inProgress"]);

        query.where.status = {
            $in: includeStatusIds
        };

        return query;
    }

    /**
     * 🟩
     * @reportIL5 - Динамика подачи идей с описанием
     * Список идей с фильтром по статусу “В реализации” с группировкой по подразделениям, и подробной информацией.
     */
    async reportIL5(query: QueryParametersInterface) {
        query.include = [
            'createdAt',
            'title',
            'description',
            'solution',
            'realEconomicEffect',
            'status',
            'responsibleForImplementationUserId',
            'plannedImplementationDate'
        ];

        const statusId = await this.getStatuses(TicketStatusCode.ideas, 'ideas.inProgress');

        if(statusId) {
            query.where.status = statusId;
        }

        return query;
    }

    /**
     * 🟩
     * @reportIL6 - Нерассмотренные идеи
     * Список идей со статусом не “Отклонено”, группировкой по подразделениям и краткой информацией.
     */
    async reportIL6(query: QueryParametersInterface) {
        query.include = [
            'createdAt',
            'title',
            'solution',
            'status',
            'direction'
        ];

        const includeStatusIds = await this.getStatuses(TicketStatusCode.ideas, [
            "ideas.submittedToCommittee",
            "ideas.submittedToBigCommittee"
        ]);

        if (includeStatusIds?.length) {
            query.where.status = {
                $in: includeStatusIds
            }
        }

        return query;
    }

    /**
     * 🟩
     * @reportIL7 - Реализованные идеи
     * Список реализованных идей с группировкой по подразделениям и краткой информацией
     */
    reportIL7(query: QueryParametersInterface) {
        query.include = [
            'createdAt',
            'title',
            'solution',
            'description',
            'responsibleForImplementationUserId',
            'implementedAt',
            'direction'
        ];

        query.where.implementedAt = {
            $exists: true
        };

        return query;
    }

    /**
     * 🟢
     * @report8 - Принятые идеи по участкам-отделам
     * Распределение поданных идей по подразделениям без формирования списка
     */
    reportIC1(query: QueryParametersInterface) {
        query.include = ["company", "branch", "direction"];

        return query;
    }

    /**
     * 🟢
     * @report9 - Принятые идеи за период
     * Распределение поданных идей по подразделениям без формирования списка с фильтром по статусу идеи.
     */
    async reportIC2(query: QueryParametersInterface) {
        query.include = ["company", "branch", "direction"];

        const statusId = await this.getStatuses(TicketStatusCode.ideas, 'ideas.inProgress');

        if (statusId) {
            query.where.status = statusId;
        }

        return query;
    }

    /** 🟢
     * @reportIC3 - Поданные идеи (топ авторов)
     * Распределение поданных идей по авторам с подсчетом общего количества
     */
    async reportIC3(query: QueryParametersInterface) {
        query.include = ["ownerId"];

        return query;
    }

    /** 🟢
     * @reportIC4 - Количество уникальных авторов и поданных идей по ним
     * Распределение поданных идей по структурным единицам с подсчетом уникальных авторов и общего количества на каждом уровне.
     */
    async reportIC4(query: QueryParametersInterface) {
        query.include = ["ownerId", "title"];

        return query;
    }

    /**🔸
     * @reportIG1 - Поданные-принятые идеи
     * Распределение поданных проблем по организационной структуре с подсчетом уникальных авторов и общего количества на каждом уровне
     */
    reportIG1(query: QueryParametersInterface) {
        return query;
    }

    /**🔸
     * @reportIG1 - Поданые идеи с разбивкой по категориям работников
     * Распределение поданых идей по подразделениям и неделям с группировкой по категории автора и подсчетом общего количества
     */
    reportIW1(query: QueryParametersInterface) {
        delete query.where.createdAt
        return query;
    }

    /** 🟥
     * @reportPL1
     *  Список всех поданных проблем
     */
    reportPL1(query: QueryParametersInterface) {
        const problemsFields = [
            'isAnonymous',
            'riskLocation',
            'riskLevel',
            'implementationDate'
        ]

        query.include = [...fullFieldsList, ...problemsFields];
        return query;
    }

    /** 🟥
     * @reportPL2 - Эскалация проблем
     * Список поданных проблем с группировкой по подразделениям и подробной информацией.
     */
    async reportPL2(query: QueryParametersInterface) {
        query.include = [
            'createdAt',
            'implementationDate',
            'title',
            'category',
            'riskLevel',
            'ownerId',
            'description',
            'responsibleUserId'
        ];

        const statusId = await this.getStatuses(TicketStatusCode.problems, 'problems.inProgress');

        if (statusId) {

            query.where.$and = [];

            query.where.$and.push({
                status: statusId
            });

            query.where.$and.push({
                implementationDate: {
                    $exist: true
                }
            })

            query.where.$and.push({
                implementationDate: {
                    $lte: DateTime.local().startOf("day").toISO()
                }
            })
        }

        return query;
    }

    /** 🟥 - 14
     * @report14 - Сроки устранения проблем
     * Список поданных проблем с фильтром по дате устранения и статусами “Решено” и “В работе”.
     */
    async reportPL3(query: QueryParametersInterface) {
        query.include = [
            'createdAt',
            'implementationDate',
            'title',
            'category',
            'riskLevel',
            'ownerId',
            'description',
            'responsibleForImplementationUserId'
        ];

        const includeStatusIds = await this.getStatuses(TicketStatusCode.problems, ["problems.implemented", "problems.inProgress"]);

        if (includeStatusIds) {
            query.where.status = {
                $in: includeStatusIds
            }
        }
        return query;
    }

    /** 🔴
     * @report15 - Поданные проблемы (топ авторов)
     * Распределение поданных проблем по авторам с подсчетом общего количества
     */
    reportPC1(query: QueryParametersInterface) {
        query.include = ["ownerId"]

        return query;
    }

    /** 🔴
     * @reportPC2 - Отчет ДРП (проблемы по категориям)
     * Распределение поданных проблем по категориям без формирования списка.
     */
    async reportPC2(query: QueryParametersInterface) {
        query.include = ["company", "branch", "direction", "category"];

        return query;
    }

    /**🔸
     * @reportPG1 - Поданные-принятые проблемы
     * Распределение поданных проблем по организационной структуре с подсчетом уникальных авторов и общего количества на каждом уровне
     */
    reportPG1(query: QueryParametersInterface) {
        return query;
    }

    /**🔸
     * @reportIG1 - Поданые проблемы с разбивкой по категориям работников
     * Распределение поданых проблем по подразделениям и неделям с группировкой по категории автора и подсчетом общего количества
     */
    reportPW1(query: QueryParametersInterface) {
        delete query.where.createdAt
        return query;
    }

    async getQuery(filter: ReportFilterState): Promise<QueryParametersInterface> {

        const methodName = this.state.selectedReportId;
        const hasMethod = this.isKeyofObject(this, methodName);
        const method = hasMethod && this[methodName as keyof this];

        if(typeof method === "function") {
            const query = this.getBaseQuery(filter);

            return await method.call(this, query)
        }
        else {
            throw new Error("Report query method is not found");
        }
    }

    getCreatedAtCondition(value: DatePickerRangeDataInterface) {
        const { start, end } = value;

        let startDate = start;
        let endDate = end;

        if (start || end) {

            if (start && !end) {
                startDate = start;
                endDate = start;
            }

            if (!start && end) {
                startDate = end;
                endDate = end;
            }

            startDate += ' 00:00';
            endDate += ' 23:59';
            return {
                $gte: startDate,
                $lte: endDate
            }
        }
        else {
            return null;
        }
    }

    getBaseQuery({ createdAt, company, branch, direction }: ReportFilterState): QueryParametersInterface {
        const createdAtCondition = this.getCreatedAtCondition(createdAt);

        return {
            order: [
                {
                    column: "createdAt",
                    direction: "desc"
                }
            ],
            take: -1,
            where: {
                ...(createdAtCondition ? { createdAt: createdAtCondition } : null),
                ...(company.id ? { company: company.id } : null),
                ...(branch.id ? { branch: branch.id } : null),
                ...(direction.id ? { direction: direction.id } : null)
            }
        }
    }

    private async getStatuses(statusCode: TicketStatusCode, codes: string | string []) {
        let query = {
            include: ['id', 'code'],
            skip: 0,
            take: -1,
            order: [
                {
                    column: 'title',
                    direction: 'asc'
                }
            ],
            where: {
                parentId: {
                    $inQuery: {
                        schema: TicketService.TICKET_STATUSES_COLLECTION,
                        where: {
                            code: statusCode
                        }
                    }
                }
            }
        };

        const statuses = await this.ticketService.getTicketsRefCollections(
            TicketService.TICKET_STATUSES_COLLECTION,
            query
        );

        if(Array.isArray(codes)) {
            return statuses?.filter(status => codes.includes(status.code)).map(status => status.id);
        }
        else {
            return statuses?.find(status => status.code === codes)?.id;
        }
    }

    private isKeyofObject<T = any>(toCheck: T, methodName: string | number | symbol): methodName is keyof T {
        const props: (string | number | symbol)[] = [];
        let obj = toCheck;
        do {
            const key = Object.getOwnPropertyNames(obj);
            props.push(...key);
        } while ((obj = Object.getPrototypeOf(obj)));

        const methods = props.sort().filter((e, i, arr) => {
            if (e != arr[i + 1]) {return true;}
        });

        return methods.includes(methodName)
    }
}